import type { TranslationValues } from '@chaika/i18n/types';
import type {
    EntryPrintFormDentalData,
    EntryPrintFormDentalToothData,
    EntryPrintFormDentalDecodingEntryData,
    EntryPrintFormDentalDecodingZoneData,
} from 'chaika-frontend-types/frontend-cms';
import type { DentalFieldData } from '@store/models/EntryFields';

import { FormulaType } from 'chaika-enums';

import {
    babyTeeth,
    ConditionId,
    permanentTeeth,
    teethParts,
    ToothCategory,
    zoneLabels,
} from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/DentalTreatment/constants';
import { getDentalConditionEntry } from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/DentalTreatment/utils/dentalConditionSource';
import { isFiveZoneTooth } from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/DentalTreatment/utils/isFiveZoneTooth';
import {
    Condition,
    DentalFormulaState,
    DentalFormulaStateItem,
    JawState,
    ToothNumber,
    ToothZone,
} from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/DentalTreatment/types';

export const DENTAL_ACTIVE_ZONE_COLOR = '#BCBCC0';

const DENTAL_JAW_ACTIVE_COLOR = '#F0F0F4';

const SURFACE_ZONES: ToothZone[] = [ToothZone.One, ToothZone.Two, ToothZone.Three, ToothZone.Four, ToothZone.Five];

const WHOLE_TOOTH_LABEL_KEY: TranslationValues = 'wholeTooth';
const ROOT_LABEL_KEY: TranslationValues = 'root';
const CROWN_LABEL_KEY: TranslationValues = 'crown';
const UPPER_JAW_HEADER_KEY: TranslationValues = 'upperGum';
const LOWER_JAW_HEADER_KEY: TranslationValues = 'lowerGum';
const SURFACE_SHORT_LABEL_KEY: TranslationValues = 'surfaceShort';
// Заголовок entry для зубов («Зуб 16») — translate('tooth') + ' ' + toothNumber.
const TOOTH_HEADER_KEY: TranslationValues = 'tooth';

type Translator = (key: TranslationValues) => string;
type MaybeConditionId = ConditionId | '' | undefined;

export function buildDentalPrintData(fieldValue: DentalFieldData, translate: Translator): EntryPrintFormDentalData {
    const permanent: DentalFormulaState = fieldValue?.[FormulaType.Permanent] ?? {};
    const baby: DentalFormulaState = fieldValue?.[FormulaType.Baby] ?? {};

    const permanentUpperJaw = buildJawRow(permanent, permanentTeeth.upperJaw);
    const permanentLowerJaw = buildJawRow(permanent, permanentTeeth.lowerJaw);
    const babyUpperJaw = buildJawRow(baby, babyTeeth.upperJaw);
    const babyLowerJaw = buildJawRow(baby, babyTeeth.lowerJaw);

    const orderedAll: ToothNumber[] = [
        ...permanentTeeth.upperJaw,
        ...permanentTeeth.lowerJaw,
        ...babyTeeth.upperJaw,
        ...babyTeeth.lowerJaw,
    ];

    const permanentDecoding = buildDecodingForFormula(permanent, orderedAll, translate);
    const babyDecoding = buildDecodingForFormula(baby, orderedAll, translate);

    // Зубы: сортируем по toothNumber (внутренняя форма), затем оборачиваем в DTO с готовым header'ом.
    const sortedToothDecoding = [...permanentDecoding, ...babyDecoding].sort(
        (a, b) => Number(a.toothNumber) - Number(b.toothNumber),
    );
    const toothEntries: EntryPrintFormDentalDecodingEntryData[] = sortedToothDecoding.map(entry => ({
        header: `${translate(TOOTH_HEADER_KEY)} ${entry.toothNumber}`,
        zones: entry.zones,
    }));

    // Десна (jaw в data-модели): по стороне (upper / lower) — отдельная entry с заголовком
    // стороны и строками permanent / milk (если у соответствующей формулы сторона активна).
    const upperJawZones = collectJawSideZones(permanent.jaw, baby.jaw, 'upper', translate);
    const lowerJawZones = collectJawSideZones(permanent.jaw, baby.jaw, 'lower', translate);

    const jawEntries: EntryPrintFormDentalDecodingEntryData[] = [];
    if (upperJawZones.length > 0) {
        jawEntries.push({ header: translate(UPPER_JAW_HEADER_KEY), zones: upperJawZones });
    }
    if (lowerJawZones.length > 0) {
        jawEntries.push({ header: translate(LOWER_JAW_HEADER_KEY), zones: lowerJawZones });
    }

    const decoding = [...jawEntries, ...toothEntries];

    return {
        babyUpperJaw,
        babyLowerJaw,
        permanentUpperJaw,
        permanentLowerJaw,
        decoding,
        permanentJawUpperColor: getJawColor(permanent.jaw, 'upper'),
        permanentJawLowerColor: getJawColor(permanent.jaw, 'lower'),
        babyJawUpperColor: getJawColor(baby.jaw, 'upper'),
        babyJawLowerColor: getJawColor(baby.jaw, 'lower'),
    };
}

export function computeToothColors(
    toothNumber: string,
    toothState: DentalFormulaStateItem | undefined,
): EntryPrintFormDentalToothData {
    const zoneCount = getZoneCount(toothNumber);

    const wholeToothId = getZoneValue(toothState, ToothZone.WholeTooth);
    const rootId = wholeToothId || getZoneValue(toothState, ToothZone.Root);
    const crownId = wholeToothId || getZoneValue(toothState, ToothZone.Crown);
    const rootCondition = getConditionForId(rootId);
    const crownCondition = getConditionForId(crownId);

    const isImplant = rootCondition === Condition.Implant;
    const isImplantProblem = rootCondition === Condition.ImplantProblem;
    const rootMissing = rootCondition === Condition.Missing;
    const crownMissing = crownCondition === Condition.Missing;

    const rootColor = isFillableCondition(rootCondition) ? DENTAL_ACTIVE_ZONE_COLOR : undefined;
    const crownColor = isFillableCondition(crownCondition) ? DENTAL_ACTIVE_ZONE_COLOR : undefined;

    const surfaceZones = zoneCount === 5 ? SURFACE_ZONES : SURFACE_ZONES.slice(0, 4);
    const surfaceConditions = surfaceZones.map(zone => getConditionForId(getZoneValue(toothState, zone)));

    const surfaceColors = surfaceConditions.map(condition =>
        isFillableCondition(condition) ? DENTAL_ACTIVE_ZONE_COLOR : '',
    );
    const surfaceMissing = surfaceConditions.map(condition => condition === Condition.Missing);

    const hasAnySurfaceFill = surfaceColors.some(Boolean);
    const hasAnySurfaceMissing = surfaceMissing.some(Boolean);

    return {
        toothNumber,
        zoneCount,
        rootColor,
        crownColor,
        isImplant,
        isImplantProblem,
        surfaceColors: hasAnySurfaceFill ? surfaceColors : undefined,
        rootMissing: rootMissing || undefined,
        crownMissing: crownMissing || undefined,
        surfaceMissing: hasAnySurfaceMissing ? surfaceMissing : undefined,
    };
}

export function isActiveConditionId(id: MaybeConditionId): id is ConditionId {
    const condition = getConditionForId(id);
    return condition !== undefined && condition !== Condition.Normal;
}

function buildJawRow(state: DentalFormulaState, toothNumbers: ToothNumber[]): EntryPrintFormDentalToothData[] {
    return toothNumbers.map(toothNumber =>
        computeToothColors(toothNumber, (state as Record<string, DentalFormulaStateItem | undefined>)[toothNumber]),
    );
}

// Внутренняя форма с toothNumber для сортировки. В buildDentalPrintData превращается в DTO
// (header + zones) после сортировки.
interface ToothDecodingEntry {
    toothNumber: string;
    zones: EntryPrintFormDentalDecodingZoneData[];
}

function buildDecodingForFormula(
    state: DentalFormulaState,
    orderedToothNumbers: ToothNumber[],
    translate: Translator,
): ToothDecodingEntry[] {
    const entries: ToothDecodingEntry[] = [];

    for (const toothNumber of orderedToothNumbers) {
        const toothState = (state as Record<string, DentalFormulaStateItem | undefined>)[toothNumber];
        const zones = buildToothDecoding(toothNumber, toothState, translate);

        if (zones.length > 0) {
            entries.push({ toothNumber, zones });
        }
    }

    return entries;
}

function buildToothDecoding(
    toothNumber: string,
    toothState: DentalFormulaStateItem | undefined,
    translate: Translator,
): EntryPrintFormDentalDecodingZoneData[] {
    if (!toothState) return [];

    const zones: EntryPrintFormDentalDecodingZoneData[] = [];

    const wholeToothId = getZoneValue(toothState, ToothZone.WholeTooth);
    if (isActiveConditionId(wholeToothId)) {
        const label = getDentalConditionEntry(wholeToothId)?.label;
        if (label) {
            zones.push({ label: translate(WHOLE_TOOTH_LABEL_KEY), value: label });
        }
    }

    const rootId = getZoneValue(toothState, ToothZone.Root);
    if (isActiveConditionId(rootId)) {
        const label = getDentalConditionEntry(rootId)?.label;
        if (label) {
            zones.push({ label: translate(ROOT_LABEL_KEY), value: label });
        }
    }

    const crownId = getZoneValue(toothState, ToothZone.Crown);
    if (isActiveConditionId(crownId)) {
        const label = getDentalConditionEntry(crownId)?.label;
        if (label) {
            zones.push({ label: translate(CROWN_LABEL_KEY), value: label });
        }
    }

    const zoneCount = getZoneCount(toothNumber);
    const groupedSurfaceId = getDistinctActiveSurfaceConditionId(toothState, zoneCount);

    if (groupedSurfaceId === null) {
        return zones;
    }

    if (groupedSurfaceId !== 'mixed') {
        const label = getDentalConditionEntry(groupedSurfaceId)?.label;
        if (label) {
            zones.push({ label: translate(CROWN_LABEL_KEY), value: label });
        }
        return zones;
    }

    const surfaceZones = zoneCount === 5 ? SURFACE_ZONES : SURFACE_ZONES.slice(0, 4);

    for (const zone of surfaceZones) {
        const id = getZoneValue(toothState, zone);
        if (isActiveConditionId(id)) {
            const conditionLabel = getDentalConditionEntry(id)?.label;
            const zoneLabelKey = getSurfaceZoneLabelKey(toothNumber, zone);
            if (conditionLabel && zoneLabelKey) {
                zones.push({
                    label: `${translate(zoneLabelKey)} ${translate(SURFACE_SHORT_LABEL_KEY)}`,
                    value: conditionLabel,
                });
            }
        }
    }

    return zones;
}

function getDistinctActiveSurfaceConditionId(
    toothState: DentalFormulaStateItem,
    zoneCount: 4 | 5,
): ConditionId | null | 'mixed' {
    const surfaceZones = zoneCount === 5 ? SURFACE_ZONES : SURFACE_ZONES.slice(0, 4);
    const activeIds: ConditionId[] = [];

    for (const zone of surfaceZones) {
        const id = getZoneValue(toothState, zone);
        if (isActiveConditionId(id)) {
            activeIds.push(id);
        }
    }

    if (activeIds.length === 0) return null;
    if (activeIds.length !== surfaceZones.length) return 'mixed';

    const first = activeIds[0];
    return activeIds.every(id => id === first) ? first : 'mixed';
}

function getSurfaceZoneLabelKey(toothNumber: string, zone: ToothZone): TranslationValues | undefined {
    const category = getToothCategory(toothNumber);
    if (!category) return undefined;
    const labels = zoneLabels[category] as Record<string, TranslationValues | undefined>;
    return labels[zone];
}

function getToothCategory(toothNumber: string): ToothCategory | undefined {
    if ((teethParts.upperLeftJaw as string[]).includes(toothNumber)) return 'upperLeftJaw';
    if ((teethParts.upperRightJaw as string[]).includes(toothNumber)) return 'upperRightJaw';
    if ((teethParts.lowerLeftJaw as string[]).includes(toothNumber)) return 'lowerLeftJaw';
    if ((teethParts.lowerRightJaw as string[]).includes(toothNumber)) return 'lowerRightJaw';
    return undefined;
}

function collectJawSideZones(
    permanentJaw: { upper?: ConditionId | ''; lower?: ConditionId | '' } | undefined,
    babyJaw: { upper?: ConditionId | ''; lower?: ConditionId | '' } | undefined,
    side: 'upper' | 'lower',
    translate: Translator,
): EntryPrintFormDentalDecodingZoneData[] {
    const zones: EntryPrintFormDentalDecodingZoneData[] = [];

    const jaws = [permanentJaw, babyJaw];

    for (const jaw of jaws) {
        const id = jaw?.[side];
        if (!isActiveConditionId(id)) continue;
        const conditionLabel = getDentalConditionEntry(id)?.label;
        if (!conditionLabel) continue;
        // Десна в расшифровке выводится одной строкой = только condition, без подзаголовка
        // формулы (label пустой → сервер пропускает `label — ` префикс).
        zones.push({ label: '', value: conditionLabel });
    }

    return zones;
}

function getJawColor(jaw: JawState | undefined, side: 'upper' | 'lower'): string | undefined {
    if (!jaw) return undefined;
    const id = jaw[side];
    if (!isActiveConditionId(id)) return undefined;
    return DENTAL_JAW_ACTIVE_COLOR;
}

function isFillableCondition(condition: Condition | undefined): boolean {
    if (!condition) return false;
    return (
        condition !== Condition.Normal &&
        condition !== Condition.Missing &&
        condition !== Condition.Implant &&
        condition !== Condition.ImplantProblem
    );
}

function getConditionForId(id: MaybeConditionId): Condition | undefined {
    if (!id) return undefined;
    return getDentalConditionEntry(id)?.condition ?? undefined;
}

function getZoneCount(toothNumber: string): 4 | 5 {
    return isFiveZoneTooth(toothNumber) ? 5 : 4;
}

function getZoneValue(toothState: DentalFormulaStateItem | undefined, zone: ToothZone): MaybeConditionId {
    if (!toothState) return undefined;

    return (toothState as Record<string, MaybeConditionId>)[zone];
}
